import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography
} from '@mui/material'
import { SortableList } from './sortable-list'
import { useGlobalMessageHandler } from 'components/common/hooks/use-user-messages-hook'
import { formatErrorMessages, type HttpResponse } from 'interfaces'

interface ReorderDialogProps<T extends { id: number | string }> {
  open: boolean
  onClose: () => void
  listId: string
  title: string
  emptyText: string
  loadingText?: string
  savingText?: string
  fetchErrorText?: string
  saveErrorText?: string
  itemIdPrefix?: string
  refetchKey?: unknown
  fetchItems: () => Promise<T[]>
  getCardText: (item: T) => string
  onSave: (orderedItems: T[]) => Promise<HttpResponse<T[]>>
  onSaved?: (result: T[]) => void | Promise<void>
}

export function ReorderDialog<T extends { id: number | string }> ({
  open,
  onClose,
  listId,
  title,
  emptyText,
  loadingText = 'Loading...',
  savingText = 'Saving...',
  fetchErrorText = 'Failed to load. Please try again.',
  saveErrorText = 'Failed to save. Please try again.',
  itemIdPrefix,
  refetchKey,
  fetchItems,
  getCardText,
  onSave,
  onSaved
}: ReorderDialogProps<T>): JSX.Element {
  const [items, setItems] = useState<T[]>([])
  const [ordered, setOrdered] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const { showErrorMessage } = useGlobalMessageHandler()

  useEffect(() => {
    if (open) void loadItems()
  }, [open, refetchKey])

  const loadItems = async (): Promise<void> => {
    setLoading(true)
    try {
      const data = await fetchItems()
      setItems(data)
      setOrdered(data)
    } catch (error) {
      console.error('ReorderDialog: failed to load items', error)
      showErrorMessage(fetchErrorText)
    } finally {
      setLoading(false)
    }
  }

  const orderChanged = (): boolean =>
    JSON.stringify(items.map(item => item.id)) !== JSON.stringify(ordered.map(item => item.id))

  const resyncAfterFailedSave = async (): Promise<void> => {
    try {
      const data = await fetchItems()
      setItems(data)
      setOrdered(data)
    } catch (error) {
      console.error('ReorderDialog: reload after a failed save also failed', error)
      setItems(ordered)
    }
  }

  const handleSaveAndClose = async (): Promise<void> => {
    if (!orderChanged()) {
      onClose()
      return
    }
    setSaving(true)
    try {
      const result = await onSave(ordered)
      if (!result.succeeded) {
        showErrorMessage(formatErrorMessages(result.errors))
        await resyncAfterFailedSave()
        return
      }
      if (onSaved != null) await onSaved(result.result ?? ordered)
      onClose()
    } catch (error) {
      console.error('ReorderDialog: failed to save order', error)
      showErrorMessage(saveErrorText)
      await resyncAfterFailedSave()
    } finally {
      setSaving(false)
    }
  }

  const handleDiscardAndClose = (): void => {
    setOrdered(items)
    onClose()
  }

  return (
    <Dialog open={open} onClose={orderChanged() ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Stack spacing={1}>
          {(loading || saving)
            ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <CircularProgress />
                  <Typography>{loading ? loadingText : savingText}</Typography>
                </Box>
              )
            : items.length === 0
              ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant='h5' gutterBottom>{title}</Typography>
                    <Typography color="text.secondary">{emptyText}</Typography>
                  </Box>
                )
              : (
                <SortableList
                  title={title}
                  items={items}
                  id={listId}
                  loading={false}
                  getCardText={getCardText}
                  itemIdPrefix={itemIdPrefix}
                  deferUpdates={true}
                  onItemsChange={setOrdered}
                />
                )
          }
        </Stack>
      </DialogContent>
      {orderChanged() && (
        <DialogActions>
          <Button onClick={handleDiscardAndClose} disabled={saving}>
            Discard Changes
          </Button>
          <Button onClick={() => { void handleSaveAndClose() }} disabled={saving} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
